import { useImperativeHandle, useState, forwardRef } from 'react'

import { OperationVariables } from '@apollo/client'
import { DocumentNode } from 'graphql'

import ListViewDisplay, {
  Props as ListViewDisplayProps,
} from './ListViewDisplay'

import { useTableData } from '@/hooks/useTableData'

export type ListRef = {
  refetch: (variables?: Partial<OperationVariables>) => void
}

export type ListViewProps = ListViewDisplayProps & {
  query: DocumentNode
  variables?: object
  customEmptyComponent?: React.ReactNode
  dataTransformer: (data: unknown) => unknown[]
  rowSelection?: {
    onChange?: (data: unknown, tableData: unknown) => void
  }
  setDataCallback?: (data: unknown) => void
  setQueryDataCallback?: (data: unknown) => void
  setSearchInput?: (value: unknown) => void
}

const ListView = forwardRef<unknown, ListViewProps>(
  (
    {
      query,
      variables,
      dataTransformer,
      rowSelection: rowSelectionOrig = null,
      setDataCallback = null,
      setQueryDataCallback = null,
      setSearchInput,
      customEmptyComponent,
      ...rest
    },
    ref
  ) => {
    const [searchString, setSearchString] = useState()

    const [loading, tableData, refetch] = useTableData(
      query,
      variables,
      dataTransformer,
      setSearchInput ? undefined : searchString,
      setDataCallback,
      setQueryDataCallback
    )

    // This is so the parent can trigger a submit
    useImperativeHandle<unknown, ListRef>(ref, () => ({ refetch }))

    const rowSelection = rowSelectionOrig
      ? {
          ...rowSelectionOrig,
          onChange: (data) => rowSelectionOrig?.onChange(data, tableData),
        }
      : null

    return (
      <ListViewDisplay
        {...rest}
        {...{
          refetch,
          setSearchInput: setSearchInput ?? setSearchString,
          loading: loading as boolean,
          tableData: tableData as [],
          rowSelection,
          variables,
          dataTransformer,
          customEmptyComponent,
        }}
      />
    )
  }
)

export default ListView
